import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

// =============================================================================
// QuoteCTA — the "Get a Free Quote" button.
//
// Why this exists:
//   • On every page except /contact, the CTA should navigate to /contact
//     (where the GoHighLevel form lives).
//   • On /contact ITSELF, the same Link does nothing (React Router skips
//     re-navigation to the current path), so a user clicking the prominent
//     Header CTA from /contact gets zero feedback. Instead, we want it to
//     smooth-scroll to the form at the top of the page.
//
// Implementation: render a Link off /contact, and on /contact swap to an
// anchor that scrolls to `id="quote"` (the wrapper around <QuoteForm/>).
// We ALSO `navigate("/contact#quote")` on click so `useLocation()` updates
// — that's what closes the mobile menu / nav dropdowns that subscribe to
// route changes via `useCloseOnRouteChange`.
// =============================================================================

export const QUOTE_FORM_ID = "quote";

export default function QuoteCTA({ className, children, "data-testid": testId, ...rest }) {
  const location = useLocation();
  const navigate = useNavigate();
  const onContactPage = location.pathname === "/contact";

  if (onContactPage) {
    return (
      <a
        href={`#${QUOTE_FORM_ID}`}
        className={className}
        data-testid={testId}
        onClick={(e) => {
          e.preventDefault();
          // Push the hash to the URL so subscribers (mobile menu, dropdowns)
          // see the location change and close themselves.
          navigate(`#${QUOTE_FORM_ID}`, { replace: false });
          // Defer the scroll until *after* the menu-close render commits.
          // Double-rAF (next animation frame after the next layout) gives
          // React time to remove the open mobile menu from the DOM — without
          // this the browser scrolls while the menu is still covering the
          // form and ends up landing past it.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const el = document.getElementById(QUOTE_FORM_ID);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            });
          });
        }}
        {...rest}
      >
        {children}
      </a>
    );
  }

  return (
    <Link to="/contact" className={className} data-testid={testId} {...rest}>
      {children}
    </Link>
  );
}
